import { compareLocalDate } from '../date/local-date.js';
import { versionInForce } from './care-plan.js';
/**
 * Where each version stands today.
 *
 * - DRAFT: not activated. Editable, applies to nothing.
 * - IN_FORCE: the one `versionInForce` picks for today.
 * - UPCOMING: activated, starting after today.
 * - SUPERSEDED: activated, and a later one has taken over (or it has ended).
 */
export function versionStandings(versions, today) {
    const inForce = versionInForce(versions, today);
    const standings = new Map();
    for (const version of versions) {
        if (!version.activated)
            standings.set(version.id, 'DRAFT');
        else if (version.id === inForce?.id)
            standings.set(version.id, 'IN_FORCE');
        else if (compareLocalDate(version.effectiveFrom, today) > 0)
            standings.set(version.id, 'UPCOMING');
        else
            standings.set(version.id, 'SUPERSEDED');
    }
    return standings;
}
/**
 * Why a draft cannot be activated, in words — or null when it can.
 *
 * **Never backdated.** A version effective before today would be the version
 * in force on days already delivered under another one, which is the
 * retroactive rewrite exit criterion 1 exists to prevent (R-3c). Today is the
 * agency's today (R-1e), passed in.
 *
 * **Never empty.** A plan with no tasks is a visit with nothing to do; the
 * caregiver's screen would be blank and the visit unverifiable later.
 *
 * **Detail where the task needs it.** A library task marked `requiresDetail`
 * — bathing, incontinence care — means nothing to a caregiver without the
 * how: "shower chair, water no hotter than lukewarm".
 */
export function activationProblem(input) {
    if (compareLocalDate(input.effectiveFrom, input.today) < 0) {
        return `A care plan cannot start before today (${input.today}): visits before then were delivered under the plan in force at the time. Choose today or a later date.`;
    }
    if (input.tasks.length === 0) {
        return 'Add at least one task. A plan with no tasks gives the caregiver nothing to do.';
    }
    const missing = input.tasks.filter((task) => task.requiresDetail && !task.instructions?.trim());
    if (missing.length > 0) {
        return `Say how to do ${missing.map((task) => task.name).join(', ')} — ${missing.length === 1 ? 'that task needs' : 'those tasks need'} instructions before the plan can start.`;
    }
    return null;
}
/** A task is the same task across versions when it is the same library task for the same service. */
const taskKey = (task) => `${task.taskLibraryId}|${task.serviceCodeId ?? '*'}`;
const sameDays = (a, b) => a.length === b.length && [...a].sort().every((day, at) => day === [...b].sort()[at]);
const blankToNull = (value) => (value?.trim() ? value.trim() : null);
/** What `to` changed relative to `from`. Order follows `to`, then `from`. */
export function diffVersions(from, to) {
    const before = new Map(from.tasks.map((task) => [taskKey(task), task]));
    const after = new Map(to.tasks.map((task) => [taskKey(task), task]));
    const added = to.tasks.filter((task) => !before.has(taskKey(task))).map((task) => task.name);
    const removed = from.tasks.filter((task) => !after.has(taskKey(task))).map((task) => task.name);
    const changed = [];
    for (const task of to.tasks) {
        const was = before.get(taskKey(task));
        if (!was)
            continue;
        const fields = [];
        if (!sameDays(was.daysOfWeek, task.daysOfWeek))
            fields.push('days');
        if (blankToNull(was.instructions) !== blankToNull(task.instructions))
            fields.push('instructions');
        if (was.isRequired !== task.isRequired)
            fields.push('required');
        if (fields.length > 0)
            changed.push({ name: task.name, fields });
    }
    const goalsBefore = new Set(from.goals.map((goal) => goal.text.trim()));
    const goalsAfter = new Set(to.goals.map((goal) => goal.text.trim()));
    return {
        added,
        removed,
        changed,
        goalsAdded: [...goalsAfter].filter((goal) => !goalsBefore.has(goal)),
        goalsRemoved: [...goalsBefore].filter((goal) => !goalsAfter.has(goal)),
        precautionsChanged: blankToNull(from.precautions) !== blankToNull(to.precautions),
    };
}
//# sourceMappingURL=care-plan-versions.js.map